'use client';

// Last-resort error boundary: catches errors thrown in app/layout.tsx itself
// or in the root rendering pipeline before app/error.tsx has a chance to
// mount. Must declare <html> and <body> because the root layout never
// rendered.

import { useEffect, useState } from 'react';

const TRANSIENT_MESSAGE = /^(Load failed|Failed to fetch|NetworkError|network error|The Internet connection appears to be offline)/i;
const TRANSIENT_NAME = /^(ChunkLoadError|AbortError)$/;
const AUTO_RESET_KEY = 'ffl_root_err_autoreset_ts';
const AUTO_RESET_WINDOW_MS = 5000;

function isTransient(error: Error): boolean {
  if (TRANSIENT_NAME.test(error.name)) return true;
  if (TRANSIENT_MESSAGE.test(error.message || '')) return true;
  return false;
}

function tryClaimAutoReset(): boolean {
  if (typeof window === 'undefined') return false;
  try {
    const last = Number(sessionStorage.getItem(AUTO_RESET_KEY) || 0);
    if (Date.now() - last < AUTO_RESET_WINDOW_MS) return false;
    sessionStorage.setItem(AUTO_RESET_KEY, String(Date.now()));
    return true;
  } catch {
    return false;
  }
}

export default function GlobalErrorBoundary({
  error,
  reset,
}: {
  error: Error & { digest?: string };
  reset: () => void;
}) {
  const [autoResetting] = useState(() => isTransient(error) && tryClaimAutoReset());

  useEffect(() => {
    const transient = isTransient(error);
    void import('@/lib/errors/log-client').then(({ logClientError }) => {
      logClientError({
        source: 'client',
        severity: transient ? 'warn' : 'fatal',
        message: error.message || 'Unknown root rendering error',
        stack: error.stack ?? null,
        digest: error.digest ?? null,
        tags: transient
          ? ['react_root_boundary', 'transient_network', autoResetting ? 'auto_reset' : 'auto_reset_skipped']
          : ['react_root_boundary'],
      });
    });
    if (autoResetting) {
      const t = setTimeout(reset, 400);
      return () => clearTimeout(t);
    }
  }, [error, reset, autoResetting]);

  if (autoResetting) {
    return (
      <html lang="en">
        <body style={{ margin: 0, padding: 0, background: '#fafafa' }} />
      </html>
    );
  }

  return (
    <html lang="en">
      <body
        style={{
          margin: 0,
          padding: 0,
          fontFamily:
            'ui-sans-serif, system-ui, -apple-system, "Segoe UI", Roboto, "Helvetica Neue", Arial',
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          background: '#fafafa',
          color: '#1a1a1a',
        }}
      >
        <main style={{ maxWidth: 420, width: '100%', textAlign: 'center', padding: '32px 16px' }}>
          <h1 style={{ fontSize: 28, fontWeight: 700, margin: 0 }}>Something went wrong</h1>
          <p style={{ marginTop: 12, fontSize: 14, lineHeight: 1.6 }}>
            The site hit an unexpected error. Please try again.
          </p>
          {error.digest ? (
            <p
              style={{
                marginTop: 8,
                fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
                fontSize: 11,
                opacity: 0.6,
              }}
            >
              Ref: {error.digest}
            </p>
          ) : null}
          <button
            onClick={reset}
            style={{
              marginTop: 24,
              padding: '10px 20px',
              borderRadius: 9999,
              border: 'none',
              cursor: 'pointer',
              background: 'linear-gradient(135deg, #ff7a00 0%, #e56a00 100%)',
              color: 'white',
              fontWeight: 600,
              fontSize: 14,
            }}
          >
            Try again
          </button>
        </main>
      </body>
    </html>
  );
}
